import { useState, type FormEvent } from "react";
import { Banner, Button, Spinner, TextField } from "@vavr/ui";
import { api, ApiError } from "../shared/api.ts";
import { passkeysSupported, signInWithPasskey } from "../shared/webauthn.ts";
import { AuthLayout } from "../auth/AuthLayout.tsx";

/** Privacy, terms and support (PMU-243): Google requires the privacy policy inside the app. */
export function LegalLinks() {
  return (
    <p className="pmu-small pmu-legal">
      <a href="https://vavr.us/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
      {" · "}<a href="https://vavr.us/terms" target="_blank" rel="noopener noreferrer">Terms</a>
      {" · "}<a href="https://vavr.us/support" target="_blank" rel="noopener noreferrer">Help</a>
    </p>
  );
}

export interface SessionResult { token?: string; expires_at?: number }

/** The fragment secret of a sign-in link, from a universal link such as https://app/auth/magic#<token>. */
export function magicTokenFrom(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.pathname.replace(/\/$/, "") !== "/auth/magic") return null;
    const t = decodeURIComponent(u.hash.slice(1));
    return /^[A-Za-z0-9_-]{16,100}$/.test(t) ? t : null;
  } catch { return null; }
}

export async function consumeMagic(token: string, client: "guardian_app" | "web"): Promise<SessionResult> {
  return api<SessionResult>("POST", "/api/v1/auth/magic/consume", { token, client });
}

/** Guardian sign-in: an emailed link first, a passkey after that (plan §4 D7). */
export function GuardianSignIn({ client, passkeys, onSession, consuming, error: outerError }: {
  client: "guardian_app" | "web"; passkeys: boolean; onSession: (r: SessionResult) => void; consuming?: boolean; error?: string | null;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [withPassword, setWithPassword] = useState(false);
  // The second step after a password, for a parent who is also staff (an authenticator app or a passkey).
  const [mfa, setMfa] = useState<{ token: string; methods: string[] } | null>(null);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    // The answer is the same whether or not the address is on file.
    await api("POST", "/api/v1/auth/magic", { email, client }).catch(() => undefined);
    setBusy(false); setSent(true);
  }

  // A password, for parents who set one in Settings (PMU-241). An account with a second factor (a parent
  // who is also staff) then gives the authenticator code or uses its passkey, as on the staff app (PMU-285).
  async function signInWithPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (!mfa) {
        const r = await api<SessionResult & { mfa_required?: boolean; mfa_token?: string; methods?: string[] }>("POST", "/api/v1/auth/login", { email, password, client });
        if (r.mfa_required) setMfa({ token: r.mfa_token!, methods: r.methods ?? [] });
        else onSession(r);
      } else {
        onSession(await api<SessionResult>("POST", "/api/v1/auth/mfa/totp", { mfa_token: mfa.token, code: code.replace(/\s/g, "") }));
      }
    } catch (err) { setError(err instanceof ApiError ? err.message : "Could not reach Vavr. Check your connection."); }
    finally { setBusy(false); }
  }

  async function mfaPasskey() {
    if (!mfa) return;
    setBusy(true); setError(null);
    try { onSession(await signInWithPasskey({ mfaToken: mfa.token, client }) as SessionResult); }
    catch (err) { setError(err instanceof ApiError ? err.message : "The passkey was not used. Try again, or use the code from your authenticator app."); }
    finally { setBusy(false); }
  }

  async function passkey() {
    setBusy(true); setError(null);
    try { onSession(await signInWithPasskey({ client }) as SessionResult); }
    catch (err) { setError(err instanceof ApiError ? err.message : "The passkey was not used. Try again, or use an email link."); }
    finally { setBusy(false); }
  }

  if (consuming) return <AuthLayout title="Signing you in"><div className="pmu-center"><Spinner label="Signing you in" /></div></AuthLayout>;
  return (
    <AuthLayout title="Parent and guardian sign-in" description="We email you a link. No password needed.">
      <div className="pmu-stack">
        {(error || outerError) && <Banner tone="danger" title={error ?? outerError} />}
        {sent ? (
          <Banner tone="success" title="Check your email">
            If this address is on file at your child's school or center, a sign-in link is on its way. {client === "guardian_app" ? "Open it on this phone. " : ""}It works once and expires in 15 minutes.
          </Banner>
        ) : withPassword && mfa ? (
          <form className="pmu-stack" onSubmit={signInWithPassword}>
            {mfa.methods.includes("totp") && (
              <>
                <TextField label="6-digit code from your authenticator app" inputMode="numeric" autoComplete="one-time-code" autoFocus required value={code} onChange={(e) => setCode(e.target.value)} />
                <Button type="submit" loading={busy} fullWidth>Continue</Button>
              </>
            )}
            {mfa.methods.includes("passkey") && passkeysSupported() && (
              <Button variant={mfa.methods.includes("totp") ? "secondary" : "primary"} icon="lock" onClick={() => void mfaPasskey()} disabled={busy} fullWidth>Use your passkey</Button>
            )}
            {!mfa.methods.includes("totp") && !passkeysSupported() && <Banner tone="info" title="This account signs in with a passkey, which this device cannot use. Use an email link instead." />}
            <Button variant="ghost" onClick={() => { setMfa(null); setCode(""); }} fullWidth>Back</Button>
          </form>
        ) : withPassword ? (
          <form className="pmu-stack" onSubmit={signInWithPassword}>
            <TextField label="Email" type="email" autoComplete="username" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" loading={busy} fullWidth>Sign in</Button>
            <Button variant="ghost" onClick={() => { setWithPassword(false); setMfa(null); }} fullWidth>Email me a link instead</Button>
          </form>
        ) : (
          <form className="pmu-stack" onSubmit={submit}>
            <TextField label="Email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button type="submit" loading={busy} fullWidth>Email me a sign-in link</Button>
            <Button variant="ghost" onClick={() => setWithPassword(true)} fullWidth>Sign in with a password</Button>
          </form>
        )}
        {sent && <Button variant="ghost" onClick={() => setSent(false)} fullWidth>Use a different email</Button>}
        {passkeys && passkeysSupported() && <Button variant="secondary" icon="lock" onClick={passkey} disabled={busy} fullWidth>Sign in with a passkey</Button>}
        <p className="pmu-small pmu-muted">For parents, guardians and the adults they trust. Children do not have accounts.</p>
        <LegalLinks />
      </div>
    </AuthLayout>
  );
}
